// SF1 — Price Change Impact Tracker.
//
// MARGIN IS THE HEADLINE, UNITS ARE CONTEXT. A price cut that lifted units and
// lost margin is a loss, and the layout has to say so: margin carries the
// colour and the ranking, units sit beside it in grey. Sorting or colouring by
// units would recommend exactly the cuts that quietly destroy the business.
//
// Effects are measured against a counterfactual — what the product was expected
// to sell anyway — not against the fortnight before, which carries the
// stockouts, the season and the competitor's moves along with the price change.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Pricing.module.css';
import { getPriceImpact } from '../services/pricingApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const CHECK_LABELS = {
  out_of_stock: 'Out of stock',
  buy_box_lost: 'Buy Box lost',
  festival: 'Festival',
  competitor_move: 'Competitor move',
};

const Money = ({ value, tone = true }) => {
  if (value === null || value === undefined) return <span className={local.blank}>—</span>;
  const cls = !tone ? local.context : value >= 0 ? local.gain : local.loss;
  return (
    <span className={cls}>
      {value >= 0 ? '+' : '−'}₹{inr.format(Math.abs(Math.round(value)))}
    </span>
  );
};

// The net is what a deal was actually worth: the lift during, less whatever it
// borrowed from the days after it.
const netOf = (episode) => {
  const effect = episode.effect || {};
  return effect.net || effect.during || {};
};

const PriceImpact = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getPriceImpact(token));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const products = data?.products || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What each price change did to margin</h2>
        {data && (
          <span className={local.muted}>
            {data.attributed_episodes} measurable
            {data.attributed_episodes === 1 ? ' change' : ' changes'}
          </span>
        )}
      </div>

      <p className={local.muted}>
        Each change is measured against what the product was expected to sell
        anyway, not against the period before it. Margin leads — a cut that
        lifted units and lost margin is ranked as a loss.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load pricing impact'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {/* Which confounder checks actually ran. A day nobody could check is not
          a clean day, and hiding that would make these figures look firmer
          than they are. */}
      {data?.confounder_checks && (
        <div className={local.checkList}>
          {data.confounder_checks.active.map((check) => (
            <span key={check} className={local.checkOn}>
              ✓ {CHECK_LABELS[check] || check} excluded
            </span>
          ))}
          {Object.entries(data.confounder_checks.unavailable).map(([check, why]) => (
            <span key={check} className={local.checkOff} title={why}>
              ✕ {CHECK_LABELS[check] || check} not checked
            </span>
          ))}
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th />
                <th>Product</th>
                <th>Change</th>
                <th className={local.numeric}>Units effect</th>
                <th className={local.numeric}>Margin effect</th>
                <th className={local.numeric}>Clean days</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td className={local.emptyCell} colSpan={6}>
                    {loading ? 'Loading…' : 'No price changes recorded yet.'}
                  </td>
                </tr>
              ) : products.map((product) => (
                <React.Fragment key={product.sku}>
                  <tr className={local.drillable}
                    onClick={() => setOpen(open === product.sku ? null : product.sku)}>
                    <td className={local.rankCell}>
                      {open === product.sku ? <FiChevronDown /> : <FiChevronRight />}
                    </td>
                    <td>{product.sku}</td>
                    <td className={local.context}>
                      ₹{inr.format(product.realised_price.first)} → ₹{inr.format(product.realised_price.last)}
                    </td>
                    <td className={`${local.numeric} ${local.context}`}>
                      {product.counts.attributed === 0 ? '—'
                        : `${product.counts.attributed} measured`}
                    </td>
                    <td className={local.numeric}>
                      {product.counts.attributed === 0 ? <span className={local.blank}>—</span>
                        : <Money value={product.episodes
                          .filter((e) => e.status === 'attributed')
                          .reduce((sum, e) => sum + (netOf(e).margin_effect || 0), 0)} />}
                    </td>
                    <td className={`${local.numeric} ${local.context}`}>{product.days}</td>
                  </tr>

                  {open === product.sku && (
                    <tr className={local.nested}>
                      <td />
                      <td colSpan={5}>
                        <table className={local.innerTable}>
                          <thead>
                            <tr>
                              <th>Episode</th>
                              <th>Dates</th>
                              <th className={local.numeric}>Units</th>
                              <th className={local.numeric}>Margin</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {product.episodes.map((episode) => {
                              const net = netOf(episode);
                              return (
                                <tr key={`${episode.kind}-${episode.start}`}>
                                  <td>
                                    <span className={episode.kind === 'deal' ? local.kindDeal : local.kindList}>
                                      {episode.kind === 'deal' ? 'Deal' : 'Price'}
                                    </span>
                                    {episode.old_price !== null && (
                                      <> ₹{inr.format(episode.old_price)} → ₹{inr.format(episode.new_price)}</>
                                    )}
                                    {episode.move_pct !== null && episode.move_pct !== undefined && (
                                      <span className={local.context}>
                                        {' '}({(episode.move_pct * 100).toFixed(1)}%)
                                      </span>
                                    )}
                                  </td>
                                  <td className={local.context}>
                                    {episode.start} → {episode.end}
                                    {episode.after_end && ` (+after to ${episode.after_end})`}
                                  </td>
                                  <td className={`${local.numeric} ${local.context}`}>
                                    {net.units_effect === undefined || net.units_effect === null
                                      ? '—' : `${net.units_effect >= 0 ? '+' : ''}${Math.round(net.units_effect)}`}
                                  </td>
                                  <td className={local.numeric}>
                                    <Money value={net.margin_effect} />
                                  </td>
                                  <td className={local.statusChip} title={episode.reason || ''}>
                                    {episode.status === 'attributed' ? '' : episode.status}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && !data.has_list_price_history && (
        <div className={local.coverage}>
          No listed-price history yet, so no price-change episodes can be cut.
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default PriceImpact;
